import {
  CallAnalyticsRecord,
  formatCallDuration,
  getCallChannel,
  getCallChannelLabel,
  getCallCreatedAt,
  getCallDurationSeconds,
  getCallEmployeeLabel,
  isMissedCall,
} from 'lib/call-analytics';
import type { LeadIncomingCall } from 'lib/lead-incoming-calls';

export type LeadCallContact = {
  id: string;
  call_id?: string | null;
  call?: CallAnalyticsRecord | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  user?: {
    id?: string | null;
    name?: string | null;
    surname?: string | null;
  } | null;
};

export function getLeadCallRecord(
  contact: LeadCallContact,
): CallAnalyticsRecord | null {
  if (contact.call) {
    return contact.call;
  }

  if (!contact.call_id) {
    return null;
  }

  return {
    id: contact.call_id,
    type: 'outgoing',
  };
}

export function getLeadCallActivityDateFromRecord(call: CallAnalyticsRecord) {
  const callDate = getCallCreatedAt(call);
  if (callDate && !Number.isNaN(callDate.getTime())) {
    return callDate;
  }

  return new Date(0);
}

export function resolveLeadActivityCall(item: {
  contact?: LeadCallContact;
  call?: LeadIncomingCall | CallAnalyticsRecord | null;
}) {
  if (item.call) {
    return item.call;
  }

  if (item.contact) {
    return getLeadCallRecord(item.contact);
  }

  return null;
}

export function getLeadCallActivityDate(contact: LeadCallContact) {
  const call = getLeadCallRecord(contact);
  if (call) {
    return getLeadCallActivityDateFromRecord(call);
  }

  const raw = contact.updatedAt || contact.createdAt;
  return raw ? new Date(raw) : new Date();
}

export function getLeadCallActivityTitle(call: CallAnalyticsRecord) {
  if (isMissedCall(call)) {
    return call.voicemail ? 'Voicemail' : 'Missed call';
  }

  if (call.type === 'outgoing') {
    return 'Outgoing call';
  }

  return 'Incoming call';
}

export function getLeadCallActivityMeta(call: CallAnalyticsRecord) {
  const durationSeconds = getCallDurationSeconds(call.payload);
  const channel = getCallChannel(call);
  const employee = getCallEmployeeLabel(call);
  const parts = [getCallChannelLabel(channel)];

  if (durationSeconds > 0) {
    parts.push(formatCallDuration(durationSeconds));
  }

  if (employee !== 'Unassigned') {
    parts.push(employee);
  }

  return parts.join(' · ');
}

export function getCallActivityNotes(
  call: CallAnalyticsRecord | null | undefined,
) {
  return (call?.notes || [])
    .map((note) => ({
      id: note.id,
      content: note.data?.content?.trim() || '',
    }))
    .filter((note) => note.content);
}
